import { useEffect, useState } from "react";

import {
  Box,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";

import { fetchOurApi } from "../../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../../helpers/accountHelpers";

const TOOL_LABELS = {
  classify_vendor_by_industry: "Vendor industry classification",
  calculate_refrigerant_emissions: "Refrigerant emissions",
  calculate_steam_emissions: "Steam emissions",
  calculate_water_emissions: "Water emissions",
  calculate_vehicle_emissions: "Vehicle emissions",
  calculate_shipping_emissions: "Shipping emissions",
  calculate_commute_emissions: "Commute emissions",
  calculate_office_utility_emissions: "Office utility emissions"
};

const formatCreatedAt = (createdAt) =>
  createdAt ? dayjs(createdAt).format("MMM D, YYYY h:mm A") : "—";

const formatResultSummary = (result) => {
  if (!result || typeof result.tCO2e !== "number") {
    return "—";
  }
  return `${result.tCO2e.toFixed(4)} tCO2e`;
};

const buildQueryString = ({ companyId, tool }) => {
  const params = new URLSearchParams({ companyId });
  if (tool) {
    params.set("tool", tool);
  }
  return params.toString();
};

const AgentActivityTab = () => {
  const [tool, setTool] = useState("");
  const [rows, setRows] = useState([]);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const companyId = getAccountId();
    // A slower response for a previous filter must not overwrite the rows
    // for the filter the user has since picked.
    const requestState = { isStale: false };
    setLoading(true);
    fetchOurApi({
      path: `/mcp-stored-results/list?${buildQueryString({ companyId, tool })}`,
      method: "GET"
    })
      .then((res) => {
        if (requestState.isStale) {
          return;
        }
        if (!res || res.error) {
          Sentry.captureMessage("agent_activity_list_failed", {
            level: "warning",
            tags: { feature: "agent-activity-tab" },
            extra: { companyId, tool, errorCode: res?.error?.code }
          });
          setError(true);
          setLoading(false);
          return;
        }
        setRows(res.rows || []);
        setWeeklyCount(res.weeklyCount || 0);
        setError(false);
        setLoading(false);
      })
      .catch((err) => {
        if (requestState.isStale) {
          return;
        }
        Sentry.captureException(err, {
          tags: { feature: "agent-activity-tab" },
          extra: { companyId, tool }
        });
        setError(true);
        setLoading(false);
      });
    return () => {
      requestState.isStale = true;
    };
  }, [tool]);

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, borderRadius: "8px", mb: 2 }}>
        <Typography variant="subtitle1" color="textPrimary">
          Calls this week
        </Typography>
        <Typography variant="h4" color="textPrimary">
          {weeklyCount}
        </Typography>
      </Paper>
      <Paper id="agent-activity" elevation={1} sx={{ p: 2, borderRadius: "8px" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" color="textPrimary">
            Agent activity
          </Typography>
          <Select
            size="small"
            value={tool}
            onChange={(event) => setTool(event.target.value)}
            displayEmpty
          >
            <MenuItem value="">All tools</MenuItem>
            {Object.entries(TOOL_LABELS).map(([toolName, label]) => (
              <MenuItem key={toolName} value={toolName}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {error && (
          <Typography variant="body2" color="textSecondary">
            Something went wrong loading your agent activity.
          </Typography>
        )}
        {!error && !loading && rows.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No agent activity yet. Connect an agent (Claude, ChatGPT, Gemini) with
            your API key to see calculations here.
          </Typography>
        )}
        {!error && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tool</TableCell>
                <TableCell>Inputs</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Called</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.resultId}>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary">
                      {TOOL_LABELS[row.tool] || row.tool}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {row.inputsSummary || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary">
                      {formatResultSummary(row.result)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">
                      {formatCreatedAt(row.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default AgentActivityTab;
